import type { GameStakeConfig, LegacyScoringType, ScoringType, StakesConfig } from './types';

export const DEFAULT_GAME_STAKE: GameStakeConfig = {
  scoringType: 'money',
  money: 10,
};

/** Formats with total-round vs per-hole stake settlement (excludes skins, nassau, match-play, wolf, best-ball). */
export const STAKE_BASIS_FORMAT_IDS = new Set([
  'stroke',
  'net-stroke',
  'stableford',
  'quota',
  'dots',
  'sixes',
  'scramble',
  'shamble',
  'vegas',
  'four-ball',
  'aggregate',
  'high-low',
  'team-stableford',
  'team-match-play',
  'lone-wolf',
  'putts-bet',
]);

export function supportsStakeBasis(formatId: string): boolean {
  return STAKE_BASIS_FORMAT_IDS.has(formatId);
}

/** Map stored scoring modes (including legacy) to current types. */
export function migrateScoringType(raw: string | undefined): ScoringType {
  switch (raw) {
    case 'money':
      return 'money';
    case 'both':
    case 'money-friendly':
      return 'both';
    case 'points':
    case 'friendly':
      return 'points';
    case 'none':
      return 'none';
    default:
      return 'points';
  }
}

export function scoringTypeLabel(type: ScoringType): string {
  switch (type) {
    case 'none':
      return 'None';
    case 'points':
      return 'Points';
    case 'money':
      return 'Money';
    case 'both':
      return 'Both';
  }
}

export function scoringTypeHint(type: ScoringType): string {
  switch (type) {
    case 'none':
      return 'Standings only — no money or in-game points.';
    case 'points':
      return 'In-game points in standings — no dollar stakes.';
    case 'money':
      return 'Dollar stakes — settle off-platform (e.g. Venmo).';
    case 'both':
      return 'In-game points plus dollar stakes.';
  }
}

/** Per-format default stakes — confirm dollar amounts with client before launch. */
const LEGACY_DEFAULTS: Record<string, Partial<GameStakeConfig>> = {
  stroke: { scoringType: 'none', scoringMode: 'gross' },
  'net-stroke': { scoringType: 'none', scoringMode: 'net' },
  'match-play': { scoringType: 'money', money: 10, matchStakeMode: 'per-match', scoringMode: 'net' },
  skins: { scoringType: 'money', money: 5, scoringMode: 'net', carryover: true },
  nassau: {
    scoringType: 'money',
    money: { front: 10, back: 10, total: 10 },
    scoringMode: 'net',
    pressesEnabled: false,
    pressStake: 10,
  },
  'team-nassau': {
    scoringType: 'money',
    money: { front: 10, back: 10, total: 10 },
    scoringMode: 'net',
    pressesEnabled: false,
    pressStake: 10,
  },
  stableford: { scoringType: 'money', money: 10, moneyPerPoint: 1 },
  'best-ball': { scoringType: 'money', money: { front: 10, back: 10, total: 10 } },
  dots: { scoringType: 'both', money: 1 },
  wolf: { scoringType: 'both', money: 1, moneyPerPoint: 1 },
  'lone-wolf': { scoringType: 'both', money: 2 },
  sixes: { scoringType: 'money', money: 2 },
  scramble: { scoringType: 'money', money: 10 },
  shamble: { scoringType: 'money', money: 10 },
  vegas: { scoringType: 'money', money: 5 },
  quota: { scoringType: 'points' },
  'four-ball': { scoringType: 'money', money: 10 },
  aggregate: { scoringType: 'money', money: 10 },
  'high-low': { scoringType: 'money', money: 10 },
  'team-stableford': { scoringType: 'money', money: 10 },
  'team-match-play': { scoringType: 'money', money: 10 },
  'ryder-cup': { scoringType: 'points' },
  'putts-bet': { scoringType: 'money', money: 5, puttsBetMode: 'lowest-total' },
};

export function defaultStakeForFormat(formatId: string): GameStakeConfig {
  return { ...DEFAULT_GAME_STAKE, ...(LEGACY_DEFAULTS[formatId] || {}) };
}

function parseOptions(raw: Record<string, unknown>): Partial<GameStakeConfig> {
  const out: Partial<GameStakeConfig> = {};
  if (raw.scoringMode === 'gross' || raw.scoringMode === 'net') out.scoringMode = raw.scoringMode;
  if (typeof raw.handicapAllowance === 'number' && Number.isFinite(raw.handicapAllowance)) {
    out.handicapAllowance = raw.handicapAllowance;
  }
  if (typeof raw.carryover === 'boolean') out.carryover = raw.carryover;
  if (raw.matchStakeMode === 'per-match' || raw.matchStakeMode === 'per-hole') {
    out.matchStakeMode = raw.matchStakeMode;
  }
  if (typeof raw.pressesEnabled === 'boolean') out.pressesEnabled = raw.pressesEnabled;
  if (raw.pressStake != null) out.pressStake = Number(raw.pressStake);
  if (raw.moneyPerPoint != null) out.moneyPerPoint = Number(raw.moneyPerPoint);
  if (raw.puttsBetMode === 'lowest-total' || raw.puttsBetMode === 'per-putt' || raw.puttsBetMode === 'per-hole') {
    out.puttsBetMode = raw.puttsBetMode;
  }
  return out;
}

/** Parse stakes from DB row (legacy numeric or new GameStakeConfig shape). */
export function stakeFromDbRow(formatId: string, raw: Record<string, unknown>): GameStakeConfig {
  if (raw.scoringType) {
    return {
      scoringType: migrateScoringType(String(raw.scoringType)),
      money: raw.money as GameStakeConfig['money'],
      pointsPerUnit: raw.pointsPerUnit != null ? Number(raw.pointsPerUnit) : undefined,
      ...parseOptions(raw),
    };
  }
  const base = defaultStakeForFormat(formatId);
  if (formatId === 'nassau' || formatId === 'team-nassau' || formatId === 'best-ball') {
    return {
      ...base,
      money: {
        front: Number(raw.front ?? 10),
        back: Number(raw.back ?? 10),
        total: Number(raw.total ?? 10),
      },
      ...parseOptions(raw),
    };
  }
  if (formatId === 'skins') {
    return { ...base, money: Number(raw.perHole ?? raw.amount ?? 5), ...parseOptions(raw) };
  }
  if (formatId === 'stableford') {
    return {
      ...base,
      money: Number(raw.perPlayer ?? raw.amount ?? 10),
      moneyPerPoint: Number(raw.moneyPerPoint ?? 1),
      ...parseOptions(raw),
    };
  }
  if (typeof raw.amount === 'number') {
    return { ...base, money: raw.amount, ...parseOptions(raw) };
  }
  return { ...base, ...parseOptions(raw) };
}

export function stakeToDbRow(config: GameStakeConfig): Record<string, unknown> {
  return {
    scoringType: config.scoringType,
    money: config.money,
    scoringMode: config.scoringMode,
    handicapAllowance: config.handicapAllowance,
    carryover: config.carryover,
    matchStakeMode: config.matchStakeMode,
    pressesEnabled: config.pressesEnabled,
    pressStake: config.pressStake,
    moneyPerPoint: config.moneyPerPoint,
    puttsBetMode: config.puttsBetMode,
  };
}

export function getStakeConfig(stakes: StakesConfig, formatId: string): GameStakeConfig {
  return stakes[formatId] ?? defaultStakeForFormat(formatId);
}

export function getScoringMode(config: GameStakeConfig): 'gross' | 'net' {
  return config.scoringMode === 'gross' ? 'gross' : 'net';
}

export function getHandicapAllowance(config: GameStakeConfig, formatId: string): number | undefined {
  if (typeof config.handicapAllowance === 'number' && Number.isFinite(config.handicapAllowance)) {
    return config.handicapAllowance;
  }
  return undefined;
}

export function getMoneyAmount(config: GameStakeConfig, leg?: 'front' | 'back' | 'total' | 'perHole'): number {
  const m = config.money;
  if (m == null) return 10;
  if (typeof m === 'number') return m;
  if (leg && m[leg] != null) return Number(m[leg]);
  if (m.perHole != null) return Number(m.perHole);
  if (m.total != null) return Number(m.total);
  return 10;
}

export function getLegStakes(config: GameStakeConfig): { front: number; back: number; total: number } {
  const m = config.money;
  if (m && typeof m === 'object') {
    return {
      front: Number(m.front ?? 10),
      back: Number(m.back ?? 10),
      total: Number(m.total ?? 10),
    };
  }
  const v = typeof m === 'number' ? m : 10;
  return { front: v, back: v, total: v };
}

/** @deprecated use getLegStakes */
export const getNassauStakes = getLegStakes;

export function appliesMoney(config: GameStakeConfig): boolean {
  return config.scoringType === 'money' || config.scoringType === 'both';
}

/** In-game point standings (Wolf, Dots, etc.) — never affects club leaderboard. */
export function appliesGamePoints(config: GameStakeConfig): boolean {
  return config.scoringType === 'points' || config.scoringType === 'both';
}

/** Wolf hole multipliers apply to friendly scoring; money-only uses $/point defaults. */
export function showWolfPointMultipliers(config: GameStakeConfig): boolean {
  return config.scoringType !== 'money';
}

/** @deprecated use appliesGamePoints */
export function appliesPoints(config: GameStakeConfig): boolean {
  return appliesGamePoints(config);
}

/** Scale net owed cents by scoring type (zero money if friendly-only). */
export function applyMoneyToNetOwed(netOwed: Record<string, number>, config: GameStakeConfig): Record<string, number> {
  if (!appliesMoney(config)) {
    return Object.fromEntries(Object.keys(netOwed).map((k) => [k, 0]));
  }
  return netOwed;
}

/** Build in-game points map from point totals (not club points). */
export function pointsFromTotals(
  playerKeys: string[],
  totals: Record<string, number>,
  config: GameStakeConfig,
): Record<string, number> {
  if (!appliesGamePoints(config)) return {};
  const out: Record<string, number> = {};
  playerKeys.forEach((k) => { out[k] = Math.round(totals[k] || 0); });
  return out;
}

export function buildDefaultStakesConfig(formatIds: string[]): StakesConfig {
  const stakes: StakesConfig = {};
  formatIds.forEach((id) => { stakes[id] = defaultStakeForFormat(id); });
  return stakes;
}

/** Type guard for stored scoring values. */
export function isLegacyScoringType(raw: string): raw is LegacyScoringType {
  return raw === 'none' || raw === 'points' || raw === 'money' || raw === 'both'
    || raw === 'friendly' || raw === 'money-friendly';
}
